/**
 * Utility to capture SVG elements from the DOM and convert them to base64 PNG images
 * Used for embedding heatmaps in ODT documents via Carbone
 */

/**
 * Converts an image URL to a base64 data URL
 * Works in browser environment
 * @param imageUrl - The URL of the image to convert
 * @param outputFormat - Output format ('image/png' or 'image/jpeg')
 * @returns Promise<string> - Base64 encoded data URL
 */
export async function imageUrlToBase64(imageUrl: string, outputFormat: "image/png" | "image/jpeg" = "image/png"): Promise<string> {
  // Try fetch first (better for CORS handling)
  try {
    const response = await fetch(imageUrl, {
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();

    // SVGs must be converted to PNG for Carbone compatibility
    if (blob.type.includes("svg")) {
      const svgText = await blob.text();
      const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
      const svgUrl = URL.createObjectURL(svgBlob);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || 200;
          canvas.height = img.naturalHeight || 80;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(svgUrl);
            reject(new Error("Could not get canvas context"));
            return;
          }
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(svgUrl);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error("Failed to render SVG to PNG"));
        };
        img.src = svgUrl;
      });
    }

    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        reject(new Error("Failed to read blob"));
      };
      reader.readAsDataURL(blob);
    });
  } catch (fetchError) {
    console.warn(`Fetch failed for ${imageUrl}, trying Image API:`, fetchError);

    // Fallback to Image API
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Enable CORS for external images

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error(`Timeout loading image: ${imageUrl}`));
      }, 10000); // 10 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        // White background for PNG transparency
        if (outputFormat === "image/png") {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL(outputFormat);
        resolve(base64);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.error(`Failed to load image: ${imageUrl}`);
        reject(new Error(`Failed to load image: ${imageUrl}`));
      };

      img.src = imageUrl;
    });
  }
}

/**
 * Converts multiple image URLs to base64 in parallel
 * @param urls - Record of key -> URL mappings
 * @returns Promise<Record<string, string | null>> - Record of key -> base64 (or null if failed)
 */
export async function convertImagesToBase64(urls: Record<string, string | undefined>): Promise<Record<string, string | null>> {
  const result: Record<string, string | null> = {};

  const entries = Object.entries(urls).filter(([, url]) => url);

  await Promise.all(
    entries.map(async ([key, url]) => {
      try {
        result[key] = await imageUrlToBase64(url!);
      } catch (error) {
        console.warn(`Failed to convert image to base64: ${key}`, error);
        result[key] = null;
      }
    })
  );

  return result;
}

/**
 * Captures an SVG element and converts it to a base64 PNG string
 * @param svgElement - The SVG element or its container div
 * @param width - Optional width for the output image
 * @param height - Optional height for the output image
 * @returns Promise<string> - Base64 encoded PNG data URL
 */
export async function svgToBase64(svgElement: SVGSVGElement | HTMLElement, width?: number, height?: number): Promise<string> {
  // If it's a container div, find the SVG inside
  let svg: SVGSVGElement;
  if (svgElement instanceof SVGSVGElement) {
    svg = svgElement;
  } else {
    const foundSvg = svgElement.querySelector("svg");
    if (!foundSvg) {
      throw new Error("No SVG element found in container");
    }
    svg = foundSvg;
  }

  // Clone the SVG to avoid modifying the original
  const clonedSvg = svg.cloneNode(true) as SVGSVGElement;

  // Get computed dimensions
  const bbox = svg.getBoundingClientRect();
  const svgWidth = width || bbox.width || 600;
  const svgHeight = height || bbox.height || 400;

  // Set explicit dimensions on the clone
  clonedSvg.setAttribute("width", String(svgWidth));
  clonedSvg.setAttribute("height", String(svgHeight));

  // Serialize the SVG to a string
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(clonedSvg);

  // Add XML declaration and namespace if missing
  if (!svgString.includes("xmlns")) {
    svgString = svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
  }

  // Create a blob and convert to data URL
  const svgBlob = new Blob([svgString], {
    type: "image/svg+xml;charset=utf-8",
  });
  const svgUrl = URL.createObjectURL(svgBlob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Create canvas and draw the image
      const canvas = document.createElement("canvas");
      canvas.width = svgWidth;
      canvas.height = svgHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        URL.revokeObjectURL(svgUrl);
        reject(new Error("Could not get canvas context"));
        return;
      }

      // White background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the SVG
      ctx.drawImage(img, 0, 0, svgWidth, svgHeight);

      // Convert to base64 PNG
      const base64 = canvas.toDataURL("image/png");

      // Cleanup
      URL.revokeObjectURL(svgUrl);

      resolve(base64);
    };

    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error("Failed to load SVG image"));
    };

    img.src = svgUrl;
  });
}

/**
 * Heatmap type for different spacing configurations
 * - 'luminance': For luminance heatmaps (road surface)
 * - 'illuminance-main': For main illuminance heatmap
 * - 'illuminance-border-p': For border illuminance heatmaps with P class (current spacing)
 * - 'illuminance-border-c': For border illuminance heatmaps with C class (more space between heatmap and legend)
 */
type HeatmapType = "luminance" | "illuminance-main" | "illuminance-border-p" | "illuminance-border-c";

/**
 * Captures a heatmap along with its legend and returns base64 PNG
 * The heatmap div contains an SVG, and the legend is a separate SVG with ID `heatmaproot-legend-${divId}`
 * @param divId - The ID of the heatmap container div (e.g., 'heatmaproot', 'heatmaproot-left')
 * @param useFixedAspectRatio - Whether to use the template's fixed aspect ratio (for Carbone PDF)
 * @param heatmapType - Type of heatmap ('luminance' or 'illuminance') for different spacing
 * @returns Promise<string | null> - Base64 PNG or null if not found
 */
export async function captureHeatmapById(divId: string, useFixedAspectRatio = false, heatmapType: HeatmapType = "luminance"): Promise<string | null> {
  console.log(`[captureHeatmapById] Attempting to capture: ${divId} (type: ${heatmapType})`);

  const container = document.getElementById(divId);
  if (!container) {
    console.warn(`[captureHeatmapById] Heatmap container not found: ${divId}`);
    return null;
  }

  const heatmapSvg = container.querySelector("svg");
  if (!heatmapSvg) {
    console.warn(`[captureHeatmapById] No SVG found in heatmap container: ${divId}`);
    return null;
  }

  // Find the legend SVG - it has ID `heatmaproot-legend-${divId}`
  const legendId = `heatmaproot-legend-${divId}`;
  const legendSvg = document.getElementById(legendId) as SVGSVGElement | null;
  console.log(`[captureHeatmapById] Found container and SVG for: ${divId}, legend: ${legendSvg ? "found" : "not found"}`);

  try {
    return await captureHeatmapWithLegend(heatmapSvg, legendSvg, useFixedAspectRatio, heatmapType);
  } catch (error) {
    console.error(`[captureHeatmapById] Failed to capture heatmap ${divId}:`, error);
    return null;
  }
}

/**
 * Captures a heatmap SVG and its legend SVG, combining them into a single image
 * @param heatmapSvg - The heatmap SVG element
 * @param legendSvg - The legend SVG element (optional)
 * @param useFixedAspectRatio - Whether to use the template's fixed aspect ratio (190.01mm x 75mm)
 * @param heatmapType - Type of heatmap ('luminance' or 'illuminance') for different spacing
 * @returns Promise<string> - Base64 encoded PNG data URL
 */
async function captureHeatmapWithLegend(
  heatmapSvg: SVGSVGElement,
  legendSvg: SVGSVGElement | null,
  useFixedAspectRatio = false,
  heatmapType: HeatmapType = "luminance"
): Promise<string> {
  // Scale factor for higher resolution output (3x recommended for PDF quality)
  // This prevents pixelation when the image is rendered in PDFs (150-300 DPI)
  const scaleFactor = 3;

  // Actual ODT template frame sizes (decompiled from content.xml), px = mm × 96/25.4
  // All frames are 160mm wide; heights differ per heatmap type.
  const TEMPLATE_WIDTH = 605; // 160mm at 96 DPI

  // Conversion factor: 1 pixel at 96 DPI = 25.4/96 mm ≈ 0.2646 mm
  // For PDF at 300 DPI with scaleFactor 3x: effective is ~0.0847 mm per canvas pixel
  const PX_TO_MM_96DPI = 25.4 / 96; // 0.2646 mm per pixel at 96 DPI

  // Get original dimensions of heatmap
  // When the container is display:none (Outprint hidden), getBoundingClientRect() returns 0.
  // Fall back to the SVG's own viewBox dimensions to avoid aspect-ratio mismatch letterboxing.
  const heatmapBbox = heatmapSvg.getBoundingClientRect();
  const heatmapViewBox = heatmapSvg.viewBox.baseVal;
  const originalHeatmapWidth = heatmapBbox.width || heatmapViewBox.width || 600;
  const originalHeatmapHeight = heatmapBbox.height || heatmapViewBox.height || 400;

  // Get dimensions of legend if it exists
  let originalLegendWidth = 0;
  let originalLegendHeight = 0;
  if (legendSvg) {
    const legendBbox = legendSvg.getBoundingClientRect();
    const legendViewBox = legendSvg.viewBox.baseVal;
    originalLegendWidth = legendBbox.width || legendViewBox.width || 320;
    originalLegendHeight = legendBbox.height || legendViewBox.height || 50;
  }

  // Log original dimensions for template adjustment
  console.log("=== HEATMAP CAPTURE DIMENSIONS ===");
  console.log("Original Heatmap (from DOM):");
  console.log(`  Width:  ${originalHeatmapWidth.toFixed(2)} px | ${(originalHeatmapWidth * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log(`  Height: ${originalHeatmapHeight.toFixed(2)} px | ${(originalHeatmapHeight * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log(`  Aspect Ratio: ${(originalHeatmapWidth / originalHeatmapHeight).toFixed(4)}:1`);
  if (legendSvg) {
    console.log("Original Legend (from DOM):");
    console.log(`  Width:  ${originalLegendWidth.toFixed(2)} px | ${(originalLegendWidth * PX_TO_MM_96DPI).toFixed(2)} mm`);
    console.log(`  Height: ${originalLegendHeight.toFixed(2)} px | ${(originalLegendHeight * PX_TO_MM_96DPI).toFixed(2)} mm`);
  }

  // Compute TEMPLATE_HEIGHT now that SVG dimensions are known.
  // Luminance: derive height from actual content (heatmap fit-to-width + legend) to avoid blank space.
  // Other types: use the measured ODT frame heights.
  const TEMPLATE_HEIGHT = (() => {
    if (heatmapType === "luminance") {
      const heatmapFitHeight = Math.ceil((TEMPLATE_WIDTH * originalHeatmapHeight) / originalHeatmapWidth);
      return heatmapFitHeight + 5 + originalLegendHeight; // heatmap + padding + legend
    }
    if (heatmapType === "illuminance-main") return 170; // 45mm
    return 151; // 40.01mm (border frames)
  })();

  let totalWidth: number;
  let totalHeight: number;
  let heatmapWidth: number;
  let heatmapHeight: number;
  let legendWidth: number;
  let legendHeight: number;
  let legendPadding: number;
  let topCrop = 0;
  let bottomCrop = 0;

  if (useFixedAspectRatio) {
    // Use fixed template dimensions
    totalWidth = TEMPLATE_WIDTH;
    totalHeight = TEMPLATE_HEIGHT;

    // Reserve a fixed height for the legend (the legend SVG is ~50px tall at viewBox size)
    const legendReservedHeight = legendSvg ? Math.min(originalLegendHeight, totalHeight * 0.35) : 0;
    legendPadding = legendSvg ? 5 : 0;

    // Calculate heatmap area (remaining space after legend)
    const heatmapAreaHeight = totalHeight - legendReservedHeight - legendPadding;

    // Scale heatmap to fit within the available area while preserving aspect ratio
    const originalAspectRatio = originalHeatmapWidth / originalHeatmapHeight;
    const availableAspectRatio = totalWidth / heatmapAreaHeight;

    if (originalAspectRatio > availableAspectRatio) {
      // Heatmap is wider than available area - fit to width
      heatmapWidth = totalWidth;
      heatmapHeight = totalWidth / originalAspectRatio;
    } else {
      // Heatmap is taller than available area - fit to height
      heatmapHeight = heatmapAreaHeight;
      heatmapWidth = heatmapAreaHeight * originalAspectRatio;
    }

    // Scale legend proportionally
    if (legendSvg) {
      const legendScale = Math.min(totalWidth / originalLegendWidth, legendReservedHeight / originalLegendHeight);
      legendWidth = originalLegendWidth * legendScale;
      legendHeight = originalLegendHeight * legendScale;
    } else {
      legendWidth = 0;
      legendHeight = 0;
    }
  } else {
    // Original behavior - use actual dimensions
    heatmapWidth = originalHeatmapWidth;
    heatmapHeight = originalHeatmapHeight;
    legendWidth = originalLegendWidth;
    legendHeight = originalLegendHeight;

    // All heatmap types share the same D3 SVG structure:
    //   viewBox: 0 0 736 (innerHeight+90)
    //   - Title at y=20
    //   - Chart area: y=30 to y=innerHeight+30   (translate(60,30))
    //   - X-axis ticks at y=innerHeight+40, labels (rotated -60°) end at ≈ innerHeight+60
    //   - 30px of blank below labels to viewBox bottom (innerHeight+90)
    //
    // Since the container is display:none, getBoundingClientRect() returns 0, so we use
    // viewBox dimensions directly (no letterboxing). Crop values are in viewBox coordinates.
    topCrop = 10; // keep 10px above title (title is at viewBox y=20)
    bottomCrop = 25; // crop 25 of the 30px blank below x-axis labels
    legendPadding = 5; // ~10px gap between labels bottom and legend top

    console.log(`[Spacing] ${heatmapType}: topCrop=${topCrop}, bottomCrop=${bottomCrop}, legendPadding=${legendPadding}`);

    totalWidth = Math.max(heatmapWidth, legendWidth);
    // Reduce total height by cropping from top and bottom
    totalHeight = heatmapHeight - topCrop - bottomCrop + (legendSvg ? legendHeight + legendPadding : 0);
  }

  // Log final canvas dimensions
  console.log("Final Canvas (output image):");
  console.log(`  Width:  ${totalWidth.toFixed(2)} px | ${(totalWidth * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log(`  Height: ${totalHeight.toFixed(2)} px | ${(totalHeight * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log(`  Aspect Ratio: ${(totalWidth / totalHeight).toFixed(4)}:1`);
  console.log(`  With ${scaleFactor}x scale: ${totalWidth * scaleFactor} x ${totalHeight * scaleFactor} px`);
  console.log("=== RECOMMENDED TEMPLATE SIZE ===");
  console.log(`  Width:  ${(totalWidth * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log(`  Height: ${(totalHeight * PX_TO_MM_96DPI).toFixed(2)} mm`);
  console.log("=================================");

  // Create canvas with scaled dimensions for higher resolution
  const canvas = document.createElement("canvas");
  canvas.width = totalWidth * scaleFactor;
  canvas.height = totalHeight * scaleFactor;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  // Scale the context to draw at higher resolution
  ctx.scale(scaleFactor, scaleFactor);

  // Transparent background — canvas is already cleared to rgba(0,0,0,0) by default

  // Helper function to draw an SVG onto the canvas
  const drawSvgToCanvas = (svg: SVGSVGElement, x: number, y: number, width: number, height: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Clone the SVG
      // Render at physical canvas size (logical × scaleFactor) so the SVG is rasterized
      // at full resolution instead of being upscaled from a small bitmap.
      const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
      clonedSvg.setAttribute("width", String(width * scaleFactor));
      clonedSvg.setAttribute("height", String(height * scaleFactor));

      // Serialize to string
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(clonedSvg);

      // Add namespace if missing
      if (!svgString.includes("xmlns")) {
        svgString = svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      // Create blob and URL
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, x, y, width, height);
        URL.revokeObjectURL(svgUrl);
        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error("Failed to load SVG image"));
      };
      img.src = svgUrl;
    });
  };

  // Draw heatmap (centered horizontally)
  // For non-fixed aspect ratio, crop top/bottom padding by drawing at negative Y
  // topCrop and bottomCrop are set in the spacing config block above
  const heatmapX = (totalWidth - heatmapWidth) / 2;
  const heatmapY = -topCrop; // Negative Y crops the top of the SVG
  await drawSvgToCanvas(heatmapSvg, heatmapX, heatmapY, heatmapWidth, heatmapHeight);

  // Draw legend below heatmap, centered horizontally
  if (legendSvg) {
    const legendX = (totalWidth - legendWidth) / 2;
    // Position legend relative to visible heatmap content (after top and bottom crop)
    const legendY = heatmapHeight - topCrop - bottomCrop + legendPadding;
    await drawSvgToCanvas(legendSvg, legendX, legendY, legendWidth, legendHeight);
  }

  // Convert to base64 PNG
  return canvas.toDataURL("image/png");
}

/**
 * Captures all heatmaps from the print view and returns them as a map
 * @param luminanceKeys - Array of luminance lane keys (e.g., ['0', '1'])
 * @param hasBorderLeft - Whether left border area exists
 * @param hasBorderRight - Whether right border area exists
 * @param borderLeftClass - The left border lighting class (e.g., 'P1', 'C1') for spacing adjustments
 * @param borderRightClass - The right border lighting class (e.g., 'P1', 'C1') for spacing adjustments
 * @returns Promise with all captured heatmaps
 */
export async function captureAllHeatmaps(
  luminanceKeys: string[],
  hasBorderLeft: boolean,
  hasBorderRight: boolean,
  borderLeftClass?: string,
  borderRightClass?: string
): Promise<{
  luminance: Record<string, string | null>;
  illuminance: string | null;
  illuminanceLeft: string | null;
  illuminanceRight: string | null;
  borderLeft: string | null;
  borderRight: string | null;
}> {
  const result = {
    luminance: {} as Record<string, string | null>,
    illuminance: null as string | null,
    illuminanceLeft: null as string | null,
    illuminanceRight: null as string | null,
    borderLeft: null as string | null,
    borderRight: null as string | null,
  };

  // Capture luminance heatmaps for each lane
  // LuminanceTable uses divId={`heatmaproot${key}-print`} which becomes the direct ID
  for (const key of luminanceKeys) {
    result.luminance[key] = await captureHeatmapById(`heatmaproot${key}-print`, true, "luminance");
  }

  // Capture main illuminance heatmap
  // IlluminanceTable uses divId="print-illuminance" -> id={`${divId}-heatmaproot`}
  result.illuminance = await captureHeatmapById("print-illuminance-heatmaproot", true, "illuminance-main");

  // Determine border heatmap types based on border classes
  // C class needs more space between heatmap and legend
  const isLeftCClass = borderLeftClass?.toUpperCase().startsWith("C") || false;
  const isRightCClass = borderRightClass?.toUpperCase().startsWith("C") || false;
  const leftBorderType: HeatmapType = isLeftCClass ? "illuminance-border-c" : "illuminance-border-p";
  const rightBorderType: HeatmapType = isRightCClass ? "illuminance-border-c" : "illuminance-border-p";

  // Capture illuminance border heatmaps from IlluminanceTable
  // IlluminanceTable uses divId="print-illuminance" -> id={`${divId}-heatmaproot-left`}
  if (hasBorderLeft) {
    result.illuminanceLeft = await captureHeatmapById("print-illuminance-heatmaproot-left", true, leftBorderType);
  }
  // IlluminanceTable uses divId="print-illuminance" -> id={`${divId}-heatmaproot-right-border`}
  if (hasBorderRight) {
    result.illuminanceRight = await captureHeatmapById("print-illuminance-heatmaproot-right-border", true, rightBorderType);
  }

  // Capture border heatmaps from IlluminanceBordersTable
  // IlluminanceBordersTable (divId="print") passes id="print-heatmaproot-left-border" to IlluminanceBordersHeatmap
  // IlluminanceBordersHeatmap then appends its own suffix, producing:
  //   left:  "print-heatmaproot-left-border-heatmaproot-left-border"
  //   right: "print-heatmaproot-left-border-heatmaproot-right-border"
  if (hasBorderLeft) {
    result.borderLeft = await captureHeatmapById("print-heatmaproot-left-border-heatmaproot-left-border", true, leftBorderType);
  }
  if (hasBorderRight) {
    result.borderRight = await captureHeatmapById("print-heatmaproot-left-border-heatmaproot-right-border", true, rightBorderType);
  }

  return result;
}
